import React, { useEffect, useState } from "react";
import styled from "styled-components";
import sanityClient from "../../sanityClient";
import tempImage from "../../assets/images/temp.jpg";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import LazyLoad from "react-lazyload";

const POSTS_PER_PAGE = 6;

const BlogMain = () => {
  const [posts, setPosts] = useState([]);
  const [featuredPost, setFeaturedPost] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    sanityClient
      .fetch(
        `*[_type == "post"] | order(publishedAt desc) {
          _id,
          title,
          "author": author->name,
          "authorImage": author->image.asset->url,
          publishedAt,
          body,
          "imageUrl": mainImage.asset->url
        }`
      )
      .then((data) => {
        const currentDate = new Date();

        // Filter out posts with a future publish date
        const filteredData = data.filter((item) => {
          const postDate = item.publishedAt ? new Date(item.publishedAt) : null;
          return postDate && postDate <= currentDate; // Ensure the post date is in the past or today
        });

        const formattedData = filteredData.map((item) => ({
          id: item._id,
          title: item.title,
          date: new Date(item.publishedAt).toLocaleDateString(),
          author: `by ${item.author}`,
          authorImage: item.authorImage || tempImage,
          excerpt: formatExcerpt(item.body),
          image: item.imageUrl || tempImage,
        }));

        setPosts(formattedData);

        if (formattedData.length > 0) {
          setFeaturedPost(formattedData[0]);
        }

        setTotalPages(Math.ceil(formattedData.length / POSTS_PER_PAGE));
      })
      .catch(console.error);
  }, []);

  const formatExcerpt = (body) => {
    const block = body.find((block) => block._type === "block");
    return block && block.children
      ? block.children
          .map((child) => child.text)
          .join(" ")
          .slice(0, 100) + "..."
      : "Read more about this story...";
  };

  const paginatePosts = () => {
    const filteredPosts = posts.filter((post) => {
      const searchQuery = searchTerm.toLowerCase();
      return (
        post.title.toLowerCase().includes(searchQuery) ||
        post.author.toLowerCase().includes(searchQuery) ||
        post.excerpt.toLowerCase().includes(searchQuery) ||
        post.date.includes(searchQuery)
      );
    });

    const startIndex = (currentPage - 1) * POSTS_PER_PAGE;
    const endIndex = startIndex + POSTS_PER_PAGE;
    return filteredPosts.slice(startIndex, endIndex);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  return (
    <>
      <Helmet>
        <title>Latest News - Blog</title>
        <meta
          name="description"
          content="Stay updated with the latest news and stories from Habitat for Humanity Buffalo."
        />
      </Helmet>
      <Wrapper>
        <Container>
          <h1>Latest News</h1>
          {featuredPost && (
            <StyledLink to={`/blog/${featuredPost.id}`}>
              <FeaturedPost>
                <LazyLoad height={400} offset={100}>
                  <FeaturedImage
                    src={featuredPost.image}
                    alt={featuredPost.title}
                  />
                </LazyLoad>
                <FeaturedContent>
                  <FeaturedTitle>{featuredPost.title}</FeaturedTitle>
                  <PostExcerpt>{featuredPost.excerpt}</PostExcerpt>
                  <Link to={`/blog/${featuredPost.id}`}>Read more</Link>
                </FeaturedContent>
              </FeaturedPost>
            </StyledLink>
          )}

          <SearchBar>
            <input
              type="text"
              placeholder="Search articles by title, author, or date..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchBar>

          <Title>All Stories</Title>
          <PostGrid>
            {paginatePosts().map((post) => (
              <StyledLink
                to={`/blog/${post.id}`}
                key={post.id}
                aria-label={`Read more about ${post.title}`}
              >
                <PostCard>
                  <LazyLoad height={200} offset={100}>
                    <PostImage src={post.image} alt={post.title} />
                  </LazyLoad>
                  <PostContent>
                    <PostTitle>{post.title}</PostTitle>
                    <PostExcerpt>{post.excerpt}</PostExcerpt>
                    <ReadMoreLink>Read more</ReadMoreLink>
                  </PostContent>
                </PostCard>
              </StyledLink>
            ))}
          </PostGrid>

          <Pagination>
            <PaginationButton
              onClick={handlePreviousPage}
              disabled={currentPage === 1}
            >
              Previous
            </PaginationButton>
            {[...Array(totalPages).keys()].map((_, idx) => (
              <PaginationNumber
                key={idx}
                onClick={() => handlePageClick(idx + 1)}
                active={currentPage === idx + 1}
              >
                {idx + 1}
              </PaginationNumber>
            ))}
            <PaginationButton
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
            >
              Next
            </PaginationButton>
          </Pagination>
        </Container>
      </Wrapper>
    </>
  );
};

// Styled Components
const Wrapper = styled.div`
  background-color: #f5f5f5;
  padding: 20px 0;
`;

const Container = styled.div`
  padding: 20px;
  max-width: 1000px;
  margin: 0 auto;
  color: #333;
`;

const Title = styled.h2`
  text-align: center;
  font-size: 2rem;
  color: var(--hhb-primary);
  margin-bottom: 20px;
`;

const StyledLink = styled(Link)`
  text-decoration: none;
  color: inherit;
`;

const FeaturedPost = styled.div`
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  margin-bottom: 40px;
`;

const FeaturedImage = styled.img`
  width: 100%;
  height: 400px;
  object-fit: cover;
`;

const FeaturedContent = styled.div`
  padding: 20px;
`;

const FeaturedTitle = styled.h2`
  font-size: 1.8rem;
  font-weight: 600;
  color: #000;
  margin-bottom: 10px;
`;

const SearchBar = styled.div`
  text-align: center;
  margin: 20px 0;

  input {
    width: 100%;
    max-width: 400px;
    padding: 10px;
    font-size: 1rem;
    border: 1px solid #ccc;
    border-radius: 5px;
  }
`;

const PostGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
`;

const PostCard = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transition: transform 0.3s;

  &:hover {
    transform: scale(1.02);
  }
`;

const PostImage = styled.img`
  width: 100%;
  height: 200px;
  object-fit: cover;
`;

const PostContent = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
`;

const PostTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 600;
  color: #000;
  margin-bottom: 10px;
`;

const PostExcerpt = styled.p`
  font-size: 1rem;
  color: #000;
  margin-bottom: auto;
`;

const ReadMoreLink = styled.span`
  color: var(--hhb-orange);
  text-decoration: underline;
  cursor: pointer;
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  margin-top: 20px;
`;

const PaginationButton = styled.button`
  background: var(--hhb-primary);
  color: #fff;
  padding: 10px 20px;
  border: none;
  border-radius: 5px;
  cursor: pointer;

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const PaginationNumber = styled.button`
  background: ${(props) => (props.active ? "var(--hhb-primary)" : "#fff")};
  color: ${(props) => (props.active ? "#fff" : "var(--hhb-primary)")};
  padding: 10px 15px;
  border: 1px solid var(--hhb-primary);
  border-radius: 5px;
  cursor: pointer;

  &:hover {
    background: var(--hhb-primary);
    color: #fff;
  }
`;

export default BlogMain;
